import { api, ApiError } from '../api.js'

export const pts2HistoryFlows = Object.freeze([
  'transactions',
  'tank-measurements',
  'in-tank-deliveries',
  'alerts',
])

const allowedFlows = new Set(pts2HistoryFlows)
const historyItemFields = new Set([
  'id', 'stationId', 'controllerId', 'vendorRecordIdentity', 'eventAt', 'receivedAt', 'data',
])
const maximumRangeMilliseconds = 366 * 24 * 60 * 60 * 1000
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const invalid = (kind = 'response') => new ApiError(`Invalid PTS-2 history ${kind}`, kind === 'query' ? 400 : 502,
  `pts2_history_${kind}_invalid`)

function positive(value) {
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 1) throw invalid('query')
  return number
}

function instant(value, kind) {
  if (value === null && kind === 'response') return null
  const date = new Date(value)
  if (!value || !Number.isFinite(date.getTime())) throw invalid(kind)
  return date.toISOString()
}

export function normalizePts2HistoryFilters(flow, controllerId, {
  stationId = null,
  from = '',
  to = '',
  cursor = '',
  limit = 50,
} = {}) {
  if (!allowedFlows.has(flow) || typeof cursor !== 'string' || cursor.length > 512) throw invalid('query')
  const normalized = {
    flow,
    stationId: stationId == null ? null : positive(stationId),
    controllerId: positive(controllerId),
    from: from ? instant(from, 'query') : '',
    to: to ? instant(to, 'query') : '',
    cursor,
    limit: Number(limit),
  }
  if (!Number.isSafeInteger(normalized.limit) || normalized.limit < 1 || normalized.limit > 200
    || Boolean(normalized.from) !== Boolean(normalized.to)) throw invalid('query')
  if (normalized.from) {
    const range = Date.parse(normalized.to) - Date.parse(normalized.from)
    if (range <= 0 || range > maximumRangeMilliseconds) throw invalid('query')
  }
  return normalized
}

function projectHistoryItem(value, filters) {
  if (!isRecord(value) || Object.keys(value).some((field) => !historyItemFields.has(field))
    || !uuid.test(value.id) || !Number.isSafeInteger(value.stationId) || value.stationId < 1
    || !Number.isSafeInteger(value.controllerId) || value.controllerId !== filters.controllerId
    || filters.stationId !== null && value.stationId !== filters.stationId
    || typeof value.vendorRecordIdentity !== 'string' || !value.vendorRecordIdentity.trim()
    || value.vendorRecordIdentity.length > 1024 || !isRecord(value.data)) throw invalid()
  return {
    id: value.id,
    stationId: value.stationId,
    controllerId: value.controllerId,
    vendorRecordIdentity: value.vendorRecordIdentity,
    eventAt: instant(value.eventAt, 'response'),
    receivedAt: instant(value.receivedAt, 'response'),
    data: structuredClone(value.data),
  }
}

export function projectPts2HistoryPage(value, filters) {
  if (!isRecord(value) || Object.keys(value).some((field) => !['items', 'nextCursor'].includes(field))
    || !Array.isArray(value.items) || value.items.length > filters.limit
    || !(value.nextCursor === null || typeof value.nextCursor === 'string' && value.nextCursor.length <= 512)) throw invalid()
  return {
    items: value.items.map((item) => projectHistoryItem(item, filters)),
    nextCursor: value.nextCursor,
  }
}

export async function getPts2History(flow, controllerId, options = {}) {
  const filters = normalizePts2HistoryFilters(flow, controllerId, options)
  const query = new URLSearchParams({ controllerId: String(filters.controllerId), limit: String(filters.limit) })
  if (filters.from) query.set('from', filters.from)
  if (filters.to) query.set('to', filters.to)
  if (filters.cursor) query.set('cursor', filters.cursor)
  const value = await api(`/pts2/${filters.flow}?${query}`, { signal: options.signal })
  return projectPts2HistoryPage(value, filters)
}
